import React from 'react';
import { clone } from 'lodash';
import styled from 'styled-components';
import { row, column, media, clearfix, container } from './globals';
import ContactUsForm from './components/ContactUsForm';
import PickList from './components/PickList';
import TileBackground from './images/DisCom_Tile.png';
import submitForm from './services/serviceClient';
import { checkRequiredField, validateEmail, validatePhone } from './services/validation';
import ReCaptcha from './components/ReCaptcha';
import common from 'common-web';

const CustomerCareFormStyled = styled.div`
    background: url(${TileBackground}) white;
    font-family: "IntroBold", "Helvetica Neue", "Helvetica", "Arial", "sans-serif" !important;
`;

const ContainerStyled = styled.div`
    ${container()};
    background: transparent;
`;

const FormContainer = styled.div`
  ${column(12, 'xs', 15, 'none')};
  ${column(10, 'lg', 15, 'none')};
  ${column(8, 'xl', 15, 'none')};
  margin: auto;
`;

const FeedbackTypeAndCategory = styled.div`
    background: white;
    padding: 40px 10px 0 10px;
    box-shadow: 0 2px 2px 0 rgba(0,0,0,0.15);
    border-radius: 3px;
    padding-bottom: ${props => props.showMap ? '59px' : '0'};
    ${clearfix()};
`;

const FeedbackStyled = styled.div`
  ${column(12, 'xs')};
  ${column(6, 'md', 20)};
`;

const TitleHeader = styled.div`
  font-size: 30px;
  text-align: center;
  padding-top: 70px;
  padding-bottom:30px;
`;

const Row = styled.div`
  ${row(0)};
`;

const Column = styled.div`
  ${column(12, 'xs')};
  ${column(12, 'sm')};
  ${column(12, 'md')};
  ${column(12, 'lg')};
  ${column(12, 'xl')};
`;

const FormControlsStyled = styled.div`
    background: white;
    box-shadow: 0 2px 2px 0 rgba(0,0,0,0.15);
    border-radius: 3px;
`;

const CaptchaStyled = styled.div`
  padding: 30px 0 18px;
  width: 304px;
  margin: auto;
`;

const SubmitContainer = styled.div`
  text-align: center;
  width: initial;
  margin-bottom: 20px;
  padding: 0 30px 37px;

  ${media.md`
    margin-bottom: 100px;
  `}
`;

const Submit = styled.button`
  margin: 12px auto;
  height: 44px;
  width: 100%;
  cursor: pointer;
  background: #02A0DA;
  border: none;
  border-radius: 2px;
  font-size: 18px;
  color: #FFFFFF;
  letter-spacing: 0;


  ${media.md`
    width: 50%;
  `}
`;

const feedbackTypes = (getLocalizedText) => {
    const typeList = [
        'Compliment',
        'Inquiry',
        'Suggestion',
        'Complaint'];

    return typeList.map(feedbackType => getLocalizedText(feedbackType, feedbackType));
};

const categoriesThatNeedLocation = (getLocalizedText) => {
    const categories = {
        Facilities: 'Facilities',
        OtherRestaurantFeedback: 'Other Restaurant Feedback',
        Packaging: 'Packaging',
        Product: 'Product',
        Service: 'Service' };

    return Object.keys(categories)
        .map(category => getLocalizedText(category, categories[category]));
};

class CustomerCare extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            feedBackType: '',
            feedBackCategory: '',
            date: '',
            time: '',
            diningType: '',
            comments: '',
            firstName: '',
            lastName: '',
            contactPreference: '',
            emailId: '',
            phone: '',
            preferredContactTime: '',
            locationId: '',
            reCaptchaResponse: null,
            reCaptchaTimeStamp: '',
            errors: {
                feedBackType: false,
                feedBackCategory: false,
                date: false,
                diningType: false,
                comments: false,
                firstName: false,
                lastName: false,
                contactPreference: false,
                emailId: false,
                phone: false,
                locationId: false,
                reCaptcha: false
            },
            locations: [],
            locationSuggestions: []
        };
    }

    handleFormUpdate= (val) => {
        this.setState(val);
    };

    onLocationSelection = (location) => {
        const stateUpdate = { locationId: location.id };

        /* errors is passed as prop, and will update visibility for selected location
         * on RestaurantInformation component; only update when required
         */
        const errors = this.state.errors;
        const resetErrors = (errors.locationId);
        if (resetErrors) {
            errors.locationId = false;
            stateUpdate.errors = errors;
        }

        this.setState(stateUpdate);
    };

    changeFeedbackType = (feedback) => {
        if (this.state.feedBackType !== feedback) {
            const errors = this.state.errors;
            if (errors.feedBackType) { errors.feedBackType = false; }
            this.setState({
                feedBackType: feedback,
                feedBackCategory: '',
                errors
            });
        }
    };

    getFeedbackCategories = () => ([
        this.getLocalizedText('Service', 'Service'),
        this.getLocalizedText('Product', 'Product'),
        this.getLocalizedText('Facilities', 'Facilities'),
    ]);

    submitFeedback = () => {
        let errors = this.state.errors; // eslint-disable-line prefer-const, max-len

        errors.feedBackType = !this.state.feedBackType;
        errors.feedBackCategory = !this.state.feedBackCategory;
        errors.firstName = !!checkRequiredField(this.state.firstName);
        errors.lastName = !!checkRequiredField(this.state.lastName);
        errors.comments = !!checkRequiredField(this.state.comments);
        errors.reCaptcha = !this.state.reCaptchaResponse;

        if (!this.state.contactPreference) {
            errors.contactPreference = true;
        } else if (this.state.contactPreference === 'Contact me back via phone') {
            errors.phone = !!validatePhone(this.state.phone);
        } else if (this.state.contactPreference === 'Contact me back via email') {
            errors.emailId = !!validateEmail(this.state.emailId);
        }

        if (this.showMap(true)) {
            errors.date = !this.state.date;
            errors.diningType = !this.state.diningType;
            errors.locationId = !this.state.locationId;
        } else {
            errors.locationId = false;
        }

        let setErrorState = false;

        Object.keys(errors).forEach((key) => {
            if (errors[key]) {
                setErrorState = true;
            }
        });

        if (setErrorState) {
            this.setState({ errors });
        } else {
            const formData = clone(this.state);
            delete formData.errors;
            submitForm(formData)
                .finally(() => this.routeToHomePage());
        }
    };

    showMap = (updateState) => {
        const show = !!categoriesThatNeedLocation(this.getLocalizedText).find(category => category === this.state.feedBackCategory); // eslint-disable-line max-len
        if (!show && updateState) {
            this.setState({ locationId: '' });  // reset to default as applicable
        }

        return show;
    };

    routeToHomePage = () => {
        const window = global.window || null; // eslint-disable-line no-use-before-define
        window.location.href = `/success.html`;
    };

    updateReCaptchaResponse = (value) => {
        const errors = clone(this.state.errors);
        const updateState = {
            reCaptchaResponse: value,
            errors
        };

        if (value) {
            errors.reCaptcha = false;
            updateState.reCaptchaTimeStamp = JSON.stringify(new Date().getTime());
        }

        this.setState(updateState);
    };

    render() {
        const showMap = this.showMap();

        let options = [];
        if (this.state.feedBackType) {
            options = this.getFeedbackCategories();
        }

        return (
            <div>
                <CustomerCareFormStyled>
                    <Row>
                        <Column>
                            <TitleHeader>{this.getLocalizedText('LeaveMessage', 'Leave a Message')}</TitleHeader>
                        </Column>
                    </Row>
                    <Row>
                        <ContainerStyled>
                            <FormContainer className="">
                                <Row>
                                    <FeedbackTypeAndCategory showMap={showMap}>
                                        <FeedbackStyled>
                                            <PickList instanceId={'feedback_type'}
                                                      label={this.getLocalizedText('FeedbackType', 'feedback type')}
                                                      value={this.state.feedBackType}
                                                      options={ feedbackTypes(this.getLocalizedText) }
                                                      handleChange={this.changeFeedbackType}
                                                      error={this.state.errors.feedBackType}
                                                      getLocalizedText={this.getLocalizedText}
                                                      locale={this.props.locale} />
                                        </FeedbackStyled>
                                        <FeedbackStyled>
                                            <PickList instanceId={'feedback_category'}
                                                      label={this.getLocalizedText('FeedbackCategory', 'feedback category')}
                                                      noResultsText={this.getLocalizedText('NoResultsText', 'Please select Feedback type')}
                                                      value={this.state.feedBackCategory}
                                                      options={options}
                                                      handleChange={
                                                          (category) => {
                                                              let errors = this.state.errors; // eslint-disable-line prefer-const, max-len
                                                              errors.feedBackCategory = false;
                                                              errors.locationId = false;
                                                              this.setState({
                                                                  feedBackCategory: category,
                                                                  errors
                                                              });
                                                          }
                                                      }
                                                      error={this.state.errors.feedBackCategory}
                                                      getLocalizedText={this.getLocalizedText}
                                                      locale={this.props.locale} />
                                        </FeedbackStyled>
                                    </FeedbackTypeAndCategory>
                                </Row>

                                <ContactUsForm errors={this.state.errors}
                                               showMap={showMap}
                                               date={this.state.date}
                                               time={this.state.time}
                                               diningType={this.state.diningType}
                                               comments={this.state.comments}
                                               firstName={this.state.firstName}
                                               lastName={this.state.lastName}
                                               contactPreference={this.state.contactPreference}
                                               emailId={this.state.emailId}
                                               phone={this.state.phone}
                                               preferredContactTime={this.state.preferredContactTime}
                                               handleFormUpdate={this.handleFormUpdate}
                                               onSearch={this.onSearch}
                                               locations={this.state.locations}
                                               locationSuggestions={this.state.locationSuggestions}
                                               clearLocationSuggestions={this.clearLocationSuggestions} // eslint-disable-line max-len
                                               onLocationSelection={this.onLocationSelection}
                                               getLocalizedText={this.getLocalizedText}
                                               locale={this.props.locale} />
                                <FormControlsStyled>
                                    <CaptchaStyled>
                                        <ReCaptcha error={this.state.errors.reCaptcha}
                                                   onChange={this.updateReCaptchaResponse}
                                                   getLocalizedText={this.getLocalizedText}
                                        />
                                    </CaptchaStyled>
                                    <SubmitContainer>
                                        <Submit type='button' onClick={this.submitFeedback}>{this.getLocalizedText('SEND', 'SEND')}</Submit>
                                    </SubmitContainer>
                                </FormControlsStyled>
                            </FormContainer>
                        </ContainerStyled>
                    </Row>
                </CustomerCareFormStyled>
            </div>
        );
    }

    onSearch = (searchParam, isSiteNumberSearch) => {
        const extraHeaders = { 'cache-control': 'no-cache' };
        const params = { lang: 'en', cntry: 'US' };

        /* define service param key based on search type */
        const serviceKey = (isSiteNumberSearch) ? 'siteNum' : 'address';
        params[serviceKey] = searchParam;

        return common.serviceClients.locationServiceClient.getNearby(params, extraHeaders)
            .then((data) => {
                const locations = [];
                const tempLocations = data.data;

                if (tempLocations && tempLocations.length) {
                    tempLocations.forEach((el) => {
                        const formattedLocation = common.lib.mappers.location.fromLocationToDTO(el);
                        locations.push(formattedLocation);
                    });
                }

                return this.setState({locations: locations});
            }, (error) => {
                if (parseInt(error.serviceStatus, 10) === 610) {
                    return this.setState({locationSuggestions: error.data});
                } else {
                    throw new Error(error);
                }
            });
    };

    clearLocationSuggestions = () => {
        this.setState({locationSuggestions: []})
    };

    getLocalizedText = (key, defaultText) => {
        return defaultText;
    };

}

export default CustomerCare;



// WEBPACK FOOTER //
// ./src/Form.js